import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { fmtQty, fmtMoneyShort, fmtPercent } from "./pivotFormat";
import { PV, cardSx } from "./pivotTokens";
import { GapBucket, Metrics } from "../../types/pivot.type";

/** Card shell shared by both overview variants (mockup `card()` + `.card-h`/`.card-b`). */
export function PivotCard({
  title,
  sub,
  action,
  children,
  flush,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
  children: ReactNode;
  /** Table cards run their content edge-to-edge instead of inside 16/20px padding. */
  flush?: boolean;
}) {
  return (
    <Box sx={{ ...cardSx, mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          px: 2.5,
          pt: 2,
          pb: flush ? 2 : 0,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15.5, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 12.5, color: PV.tx3, mt: "1px" }}>{sub}</Typography>
          )}
        </Box>
        {action}
      </Box>
      <Box sx={flush ? undefined : { px: 2.5, pt: 2, pb: 2.5 }}>{children}</Box>
    </Box>
  );
}

/** Two-column overview body: tiles left, ranking right; stacks under 900px (mockup `.pv-ovw`). */
const OverviewSplit = ({ left, right }: { left: ReactNode; right: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "stretch",
      gap: 3.25,
      flexDirection: { xs: "column", md: "row" },
    }}
  >
    <Box sx={{ flex: "1 1 0", minWidth: 0 }}>{left}</Box>
    <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
      {right}
    </Box>
  </Box>
);

// ---- KPI tiles -------------------------------------------------------------

interface KpiTileProps {
  label: string;
  value: string;
  unit?: string;
  color?: string;
  /** The ยอดขาย tile is the one the page is about, so it gets the filled treatment. */
  highlight?: boolean;
}

const KpiTile = ({ label, value, unit, color, highlight }: KpiTileProps) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: highlight ? PV.indigoBorder : PV.line,
      bgcolor: highlight ? PV.indigoTint : PV.card,
      borderRadius: "10px",
      px: 1.9,
      py: 1.75,
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: 12.5, color: PV.tx2, lineHeight: 1.4 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 21,
        fontWeight: 700,
        letterSpacing: "-0.025em",
        fontVariantNumeric: "tabular-nums",
        mt: 1.1,
        color: highlight ? PV.indigo : (color ?? PV.tx),
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
      {unit && (
        <Typography
          component="span"
          sx={{ fontSize: 13, fontWeight: 500, color: PV.tx2, ml: 0.6 }}
        >
          {unit}
        </Typography>
      )}
    </Typography>
  </Box>
);

const TileGrid = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
      gap: 1.5,
      alignContent: "start",
    }}
  >
    {children}
  </Box>
);

export function PivotKpiTiles({
  rowCount,
  unit,
  grandTotal,
  wasteCapable,
  carryoverCapable,
}: {
  rowCount: number;
  unit: string;
  grandTotal: Metrics;
  wasteCapable: boolean;
  carryoverCapable: boolean;
}) {
  return (
    <TileGrid>
      <KpiTile label={`จำนวน${unit}`} value={fmtQty(rowCount)} unit={unit} />
      <KpiTile label="ขาย" value={fmtQty(grandTotal.sold_qty)} unit="ชิ้น" />
      <KpiTile
        label="ของเสีย"
        value={wasteCapable ? fmtQty(grandTotal.waste_qty) : "—"}
        unit={wasteCapable ? "ชิ้น" : undefined}
        color={PV.waste}
      />
      <KpiTile
        label="%ของเสีย"
        value={grandTotal.waste_percent === null ? "—" : fmtPercent(grandTotal.waste_percent)}
        color={PV.waste}
      />
      {/* Gated on its own capability, not on `waste`: a scope routinely records what came
          back expired and says nothing about what was left on the shelf. */}
      <KpiTile
        label="เหลือบนเชลล์"
        value={carryoverCapable ? fmtQty(grandTotal.carryover_qty) : "—"}
        unit={carryoverCapable ? "ชิ้น" : undefined}
        color={PV.leftover}
      />
      <KpiTile label="ยอดขาย" value={fmtMoneyShort(grandTotal.sales_amount)} highlight />
    </TileGrid>
  );
}

// ---- Horizontal ranking bars ----------------------------------------------

export interface HBarRow {
  label: string;
  value: number;
  /** Right-hand readout; defaults to "<value> ชิ้น". */
  display?: ReactNode;
  color?: string;
}

/**
 * Label / track / value rows (mockup `.hbars`). Deliberately hand-built rather than a
 * recharts BarChart: the form here is a labelled progress row, not a plotted axis, and
 * recharts' vertical-layout bars cannot produce the right-aligned readout column.
 */
export function PivotHBars({ rows, emptyText }: { rows: HBarRow[]; emptyText?: string }) {
  if (!rows.length) {
    return (
      <Typography sx={{ py: 5, textAlign: "center", color: PV.tx3, fontSize: 13.5 }}>
        {emptyText ?? "ไม่มีข้อมูลตามตัวกรอง"}
      </Typography>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 0.0001);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "96px 1fr 92px", sm: "150px 1fr 112px" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Typography
            title={r.label}
            sx={{
              fontSize: 13,
              color: PV.tx2,
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {r.label}
          </Typography>
          <Box sx={{ height: 20, bgcolor: PV.line2, borderRadius: "5px", overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${Math.max(0, (r.value / max) * 100)}%`,
                bgcolor: r.color ?? PV.indigo,
                borderRadius: "5px",
                transition: "width .5s cubic-bezier(.22,1,.36,1)",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {r.display ?? (
              <>
                {fmtQty(r.value)}
                <Typography component="span" sx={{ fontSize: 11, color: PV.tx3, ml: 0.5 }}>
                  ชิ้น
                </Typography>
              </>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Overview card for the three store tabs and the product tab. */
export function PivotSummaryOverview({
  title,
  rowCount,
  unit,
  grandTotal,
  wasteCapable,
  carryoverCapable,
  bars,
}: {
  title: string;
  rowCount: number;
  unit: string;
  grandTotal: Metrics;
  wasteCapable: boolean;
  carryoverCapable: boolean;
  bars: HBarRow[];
}) {
  return (
    <PivotCard title={title}>
      <OverviewSplit
        left={
          <PivotKpiTiles
            rowCount={rowCount}
            unit={unit}
            grandTotal={grandTotal}
            wasteCapable={wasteCapable}
            carryoverCapable={carryoverCapable}
          />
        }
        right={<PivotHBars rows={bars} />}
      />
    </PivotCard>
  );
}

// ---- Gap tiles (visit tab) -------------------------------------------------

function GapTile({
  label,
  count,
  active,
  onClick,
  neutral,
  fullWidth,
}: {
  label: ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
  neutral?: boolean;
  fullWidth?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        gridColumn: fullWidth ? "1 / -1" : undefined,
        textAlign: "left",
        font: "inherit",
        cursor: "pointer",
        bgcolor: active ? PV.indigoTint : PV.card,
        border: "1px solid",
        borderColor: active ? PV.indigo : PV.line,
        boxShadow: active ? `0 0 0 3px rgba(91,95,224,.12)` : "none",
        borderRadius: "10px",
        px: 1.9,
        py: 1.75,
        "&:hover": { borderColor: PV.indigoBorder, bgcolor: PV.indigoTint },
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: PV.tx2 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 21,
          fontWeight: 700,
          letterSpacing: "-0.025em",
          fontVariantNumeric: "tabular-nums",
          mt: 0.9,
          color: neutral || count === 0 ? PV.tx : PV.waste,
        }}
      >
        {fmtQty(count)}
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: PV.tx2, ml: 0.6 }}>
          ร้าน
        </Typography>
      </Typography>
    </Box>
  );
}

export function PivotGapOverview({
  buckets,
  scopeStoreCount,
  active,
  onSelect,
  topRows,
}: {
  buckets: GapBucket[];
  scopeStoreCount: number;
  active: number;
  onSelect: (threshold: number) => void;
  topRows: { label: string; days: number; capped: boolean }[];
}) {
  // The thresholds are a system setting the server applies, so the tiles follow its buckets.
  const thresholds = buckets.map((bucket) => bucket.min_days);
  const byThreshold = new Map(buckets.map((b) => [b.min_days, b.store_count]));

  return (
    <PivotCard title="ร้านที่ขาดการเข้ามานาน">
      <OverviewSplit
        left={
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5, alignContent: "start" }}>
            {thresholds.map((t) => (
              <GapTile
                key={t}
                label={
                  <>
                    ไม่ได้เข้าเกิน{" "}
                    <Box component="b" sx={{ color: PV.tx, fontWeight: 700 }}>
                      {t}
                    </Box>{" "}
                    วัน
                  </>
                }
                count={byThreshold.get(t) ?? 0}
                active={active === t}
                onClick={() => onSelect(active === t ? 0 : t)}
              />
            ))}
            <GapTile
              label="ทุกร้านในขอบเขต"
              count={scopeStoreCount}
              active={active === 0}
              onClick={() => onSelect(0)}
              neutral
              fullWidth
            />
          </Box>
        }
        right={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, color: PV.tx2, fontWeight: 600 }}>
              ห่างจากรอบล่าสุดนานที่สุด · Top 10
            </Typography>
            <PivotHBars
              rows={topRows.slice(0, 10).map((r) => ({
                label: r.label,
                // Capped rows (nothing found in the 200-day lookback) come back as 201; clamp
                // the bar to the cap so one unknown-idle store doesn't flatten every other bar.
                value: Math.min(r.days, 200),
                color: r.days > 15 ? PV.waste : r.days > 7 ? PV.leftover : PV.indigo,
                display: (
                  <>
                    {r.capped ? "200+" : fmtQty(r.days)}
                    <Typography component="span" sx={{ fontSize: 11, color: PV.tx3, ml: 0.5 }}>
                      วัน
                    </Typography>
                  </>
                ),
              }))}
            />
          </Box>
        }
      />
    </PivotCard>
  );
}
